import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../config';
import Spinner from '../components/Spinner';

const PaymentStatus = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useContext(AuthContext);
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  
  // If we just navigated from Cart/Retry, we might have the invoice URL in state
  const invoiceUrl = location.state?.invoice_url;

  const fetchOrder = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/${orderId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrder(data);
      } else if (res.status === 404) {
        console.error('Order not found in DB! Order ID:', orderId);
        // navigate('/account/dashboard');
      } else {
        console.error('Server returned:', res.status);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    
    // Poll every 10 seconds if still pending
    const interval = setInterval(() => {
      if (order?.status === 'pending') {
        fetchOrder();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [orderId, token, order?.status]);

  // If this page was opened for the first time with an invoice_url, 
  // immediately redirect them to pay in the SAME tab. 
  // We use sessionStorage to ensure we ONLY auto-redirect ONCE.
  // When they hit "Back", they will land HERE, sessionStorage will be true, stopping the loop.
  useEffect(() => {
    const hasRedirected = sessionStorage.getItem(`redirected_${orderId}`);
    if (invoiceUrl && order?.status === 'pending' && hasRedirected !== 'true') {
      sessionStorage.setItem(`redirected_${orderId}`, 'true');
      window.location.href = invoiceUrl;
    }
  }, [invoiceUrl, order?.status, orderId]);

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    setCancelling(true);
    try {
      const res = await fetch(`${API_URL}/api/cancel-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ orderId })
      });
      if (res.ok) {
        fetchOrder(); // refresh status
      }
    } catch (err) {
      console.error(err);
    }
    setCancelling(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <Spinner text="Loading payment status..." />
      </div>
    );
  }

  if (!order) return null;

  const isPending = order.status === 'pending';
  const isCompleted = order.status === 'COMPLETED' || order.status === 'Paid';
  const isFailed = order.status === 'EXPIRED' || order.status === 'CANCELLED';

  return (
    <div className="min-h-screen flex flex-col justify-center items-center px-4 py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-2xl w-full z-10 flex flex-col items-center text-center">
        
        {/* Status Icon & Title */}
        <div className="mb-8 flex flex-col items-center">
          {isPending && (
            <>
              <div className="w-20 h-20 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-6 shadow-[0_0_30px_rgba(240,185,11,0.2)]"></div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Payment Not Yet Received</h1>
              <p className="text-on-surface-variant max-w-md">
                We are waiting for payment to be detected on the blockchain. This page updates automatically when the status changes.
              </p>
            </>
          )}
          
          {isCompleted && (
            <>
              <div className="w-20 h-20 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(34,197,94,0.2)]">
                <span className="material-symbols-outlined text-[40px]">check</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Payment Received!</h1>
              <p className="text-green-400/80 max-w-md">
                Your blockchain transaction has been confirmed. Thank you for your purchase.
              </p>
            </>
          )}

          {isFailed && (
            <>
              <div className="w-20 h-20 bg-red-500/20 text-red-400 rounded-full flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(239,68,68,0.2)]">
                <span className="material-symbols-outlined text-[40px]">close</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Order {order.status === 'CANCELLED' ? 'Cancelled' : 'Failed/Expired'}</h1>
              <p className="text-red-400/80 max-w-md">
                This payment session is no longer active. You can retry the payment from your orders dashboard.
              </p>
            </>
          )}
        </div>

        {/* Order Details Card */}
        <div className="w-full bg-surface-container border border-white/5 rounded-3xl p-6 md:p-8 shadow-xl mb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 bg-surface-container rounded-lg border border-white/5 mb-6">
              <div>
                <p className="text-on-surface-variant text-[14px]">Amount Due:</p>
                <p className="text-[24px] font-bold text-white">${order.amount} USD</p>
              </div>
              <div className="text-left md:text-right mt-4 md:mt-0">
                <p className="text-on-surface-variant text-[14px]">Crypto Network:</p>
                <p className="text-[18px] font-bold text-primary">{order.crypto_used}</p>
              </div>
            </div>

            {invoiceUrl && (
              <a 
                href={invoiceUrl}
                className="w-full bg-primary text-[#131418] font-bold py-3 rounded-lg flex items-center justify-center gap-2 hover:shadow-[0_0_15px_rgba(240,185,11,0.5)] transition-all mb-4"
              >
                <span className="material-symbols-outlined">account_balance_wallet</span>
                Resume Payment on NOWPayments
              </a>
            )}
          <div className="flex justify-between items-center py-4 border-b border-white/5">
            <span className="text-on-surface-variant text-[14px]">Transaction ID:</span>
            <span className="text-white font-mono text-[14px]">{order.tx_id}</span>
          </div>
          <div className="flex justify-between items-center py-4 border-b border-white/5">
            <span className="text-on-surface-variant text-[14px]">Status:</span>
            <span className={`px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest ${
              isCompleted ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 
              isFailed ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 
              'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
            }`}>
              {order.status === 'pending' ? 'PAYMENT NOT STARTED' : order.status}
            </span>
          </div>
          <div className="flex justify-between items-center py-4">
            <span className="text-on-surface-variant text-[14px]">Amount:</span>
            <span className="text-white font-bold">${parseFloat(order.amount).toFixed(2)} USD</span>
          </div>
          
          {isPending && (
            <div className="mt-4 pt-4 border-t border-white/5 text-[12px] text-primary/80 font-medium">
              This page refreshes automatically. You can safely leave and check your orders later.
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {isPending && (
            <>
              {invoiceUrl && (
                <a href={invoiceUrl} className="bg-primary text-[#131418] font-bold px-8 py-3 rounded-full text-[14px] hover:shadow-[0_0_15px_rgba(240,185,11,0.4)] transition-all text-center">
                  Pay Now
                </a>
              )}
              <button 
                onClick={handleCancel}
                disabled={cancelling}
                className="bg-surface-container border border-white/10 text-white font-bold px-8 py-3 rounded-full text-[14px] hover:bg-white/5 transition-all text-center disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </>
          )}
          
          <Link to="/account/my-orders" className={isPending && invoiceUrl ? "bg-transparent text-white border border-white/10 font-bold px-8 py-3 rounded-full text-[14px] hover:bg-white/5 transition-all text-center" : "bg-primary text-[#131418] font-bold px-8 py-3 rounded-full text-[14px] hover:shadow-[0_0_15px_rgba(240,185,11,0.4)] transition-all text-center"}>
            View My Orders
          </Link>
        </div>

      </div>
    </div>
  );
};

export default PaymentStatus;
